import React from 'react';
import { FilterButtons } from './FilterButtons';
import { useState } from 'react';
import { useCountOfActiveJobs, useLiveJobsCountByDays } from '../../../data/jobDetailsNewCollection';
import { useEmployerAndPlans } from '../../../data/dashboardInightHub';
import Last6MonthAggregateData from '../../../charts/Last6MonthAggregateData';
import JobInteractionsChart from './A_Components/JobInteractionsChart';
import JobInteractionsByType from './A_Components/JobInteractionByType';
import ByPlatformCharts from './A_Components/ViewsByPlatformCharts';
import { useAllListingsJobForNew } from '../../../data/listings';
import ExpiringJobs from './A_Components/ExpiringJobs';
import { formatUSNumber } from './helper';


const InteractionsStats = () => {
    const [selectedDays, setSelectedDays] = useState(14);
    const [interactionCount, setInteractionCount] = useState()

    const { data } = useCountOfActiveJobs()
    const { data: jobsCountsForDays } = useLiveJobsCountByDays(selectedDays)


    const { data: jobInteractionsStats }: any = useEmployerAndPlans("interactionStats")
    const { data: allJobs, isLoading } = useAllListingsJobForNew();

    const firestoreTimestampToDate = (timestamp: any) => {
        return new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1e6);
    };

    // Main function
    const getExpiringJobsInNext7Days = (jobs: any) => {
        const now = new Date();
        const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

        return jobs
            .map((job:any) => {
                const liveDate = firestoreTimestampToDate(job.makeLiveDate);
                const expiryDate = new Date(liveDate.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days validity

                return {
                    id: job.id,
                    title: job.jobName,
                    employer: job.companyFullName,
                    type: job.solutionType?.toUpperCase() || "N/A",
                    liveDate: liveDate.toISOString().split("T")[0],
                    expiryDate: expiryDate.toISOString().split("T")[0],
                };
            })
            .filter((job: any) => {
                const expiryDate = new Date(job.expiryDate);
                return expiryDate >= now && expiryDate <= sevenDaysFromNow;
            });
    };

    const expiringJobs = getExpiringJobsInNext7Days(allJobs||[]).filter((job:any)=> job.type !== "BPO");



    const totals: any = jobInteractionsStats?.interactionData?.reduce((acc: any, item: any) => {
        const { solutionType, platformStats } = item;
        const totalView = platformStats?.reduce((sum: any, p: any) => sum + (p.viewCount || 0), 0);
        const totalApply = platformStats?.reduce((sum: any, p: any) => sum + (p.applyCount || 0), 0);

        acc[solutionType] = {
            totalViewCount: totalView,
            totalApplyCount: totalApply,
        };
        return acc;
    }, {});


    const combined: any = {};

    jobInteractionsStats?.interactionData?.forEach(({ platformStats }: any) => {
        platformStats.forEach(({ platform, viewCount, applyCount }: any) => {
            if (platform === "Total") return; // ignore total
            if (!combined[platform]) combined[platform] = { viewCount: 0, applyCount: 0 };
            combined[platform].viewCount += viewCount || 0;
            combined[platform].applyCount += applyCount || 0;
        });
    });

    // 2️⃣ Compute totals
    const totalViews: any = Object.values(combined).reduce((sum: any, p: any) => sum + p.viewCount, 0);
    const totalClicks: any = Object.values(combined).reduce((sum: any, p: any) => sum + p.applyCount, 0);

    // 3️⃣ Build the arrays with percentages
    const viewsByPlatform = Object.entries(combined).map(([name, { viewCount }]: any) => ({
        name,
        value: viewCount,
        percentage: Number(((viewCount / totalViews) * 100).toFixed(1)),
    }));

    const clicksByPlatform = Object.entries(combined).map(([name, { applyCount }]: any) => ({
        name,
        value: applyCount,
        percentage: Number(((applyCount / totalClicks) * 100).toFixed(1)),
    }));

    const getPeriodLabel = (days:number) => {
        if (days === 90) return "last quarter";
        if (days === 365) return "last year";
        return `last ${days} days`;
    };



    return (
        <div className='bg-white pr-2'>
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-instant-teams-blue-Main text-foreground">
                    Interactions Stats
                </h2>
                <FilterButtons selected={selectedDays} onSelect={setSelectedDays} />
            </div>
            <div className="mt-6 border rounded-lg  border-[#bad5f1]  bg-[#E6F1FC] p-8">
                {/* Summary Header */}
                <h3 className="text-[22px] font-semibold text-instant-teams-blue-Main mb-6">Summary</h3>

                {/* Top Stats */}
                <div className="grid  grid-cols-4 gap-6">
                    <div>
                        <p className="text-sm text-gray-500">Total Active Jobs</p>
                        <p className="text-2xl font-bold text-[#183853]">{data?.totalLive || 0}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Job Board</p>
                        <p className="text-2xl font-bold text-[#183853]">{data?.totalJobBoard || 0}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">BPO</p>
                        <p className="text-2xl font-bold text-[#183853]">{data?.totalBPO || 0}</p>
                    </div>

                    <div>
                        <p className="text-sm text-gray-500">
                            Total Jobs Posted 
                            <span className='text-sm'>{ ` ( ${getPeriodLabel(selectedDays)} )` }</span>
                        </p>
                        <p className="text-2xl font-bold text-[#183853]">{jobsCountsForDays || 0}</p>
                    </div>

                </div>
            </div>

            <div className="mt-5 border  border-gray-200  bg-white rounded-lg  p-8">
                <div className="grid  grid-cols-4 gap-6">
                    <div>
                        <p className="text-sm text-gray-500">Job Board Views</p>
                        <p className="text-2xl font-bold text-[#183853]">{new Intl.NumberFormat("en-US").format(interactionCount?.dh?.views_count||0)}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">BPO Views</p>
                        <p className="text-2xl font-bold text-[#183853]">{new Intl.NumberFormat("en-US").format(interactionCount?.bpo?.views_count||0)}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Job Board Clicks</p>
                        <p className="text-2xl font-bold text-[#183853]">{new Intl.NumberFormat("en-US").format(interactionCount?.dh?.apply_count||0)}</p>
                    </div>

                    <div>
                        <p className="text-sm text-gray-500">BPO Clicks</p>
                        <p className="text-2xl font-bold text-[#183853]">{new Intl.NumberFormat("en-US").format(interactionCount?.bpo?.apply_count||0)}</p>
                    </div>
                </div>
            </div>
            
            <div className='mt-5'>

                <JobInteractionsByType daysSelected={selectedDays} setInteractionCount={setInteractionCount} />
            </div>
            <div>
                <JobInteractionsChart />
            </div>
            <div className='grid gap-5 grid-cols-2 mt-5'>
                <ByPlatformCharts
                    chartData={viewsByPlatform}
                    chartTitle="Views by Platform"
                    chartDescrpition="Distribution across iOS, Android, and Web"
                />
                <ByPlatformCharts
                    chartData={clicksByPlatform}
                    chartTitle="Clicks by Platform"
                    chartDescrpition="Distribution across iOS, Android, and Web"
                />
            </div>

            <div className='mt-5'>
                <ExpiringJobs expiringJobs={expiringJobs} />
            </div>
        </div>
    );
}

export default InteractionsStats;
